const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/deviceReducer.ts';
import Immutable from 'immutable';
import { DEVICESCREEN_CHANGED } from '../actions/deviceActions';
import { IReliableDeviceInfo, NATIVEPORT_REL_DEVICEINFO } from '@cistec/common/shared_defs';
import { DeviceOrientation, DeviceType } from '@cistec/common_web/defs/device';

// just beeing lazy ...
declare const require: any;
const MobileDetect = require('mobile-detect');

/**
 * Utility function to retrieve the initial device infos
 *
 * @param windowObj fake or real window object able to return a user agent string
 * @returns collection of parsed device properties that could be detected
 */
function collectDeviceInfos(windowObj: any) {
    const md = new MobileDetect(windowObj.navigator.userAgent);
    let type: DeviceType = DeviceType.DESKTOP;
    if (md.mobile()) {
        type = DeviceType.PHONE;
    }
    if (md.tablet()) {
        type = DeviceType.TABLET;
    }
    if (md.phone()) {
        type = DeviceType.PHONE;
    }
    const width: number = windowObj.innerWidth;
    const height: number = windowObj.innerHeight;
    const orientation = Number(width) / height < 1.25 ? DeviceOrientation.PORTRAIT : DeviceOrientation.LANDSCAPE;

    // collect additional device and browser data for analytics
    let osVersion: string;
    switch (md.os()) {
        case 'WindowsPhoneOS':
            osVersion = md.versionStr('Windows Phone');
            break;
        case 'AndroidOS':
            osVersion = md.versionStr('Android');
            break;
        case 'BlackberryOS':
            osVersion = md.versionStr('Blackberry');
            break;
        default:
            osVersion = md.versionStr(md.os());
    }

    let agent: string;
    let agentVersion: string;
    if (!!md.userAgent()) {
        agent = md.userAgent();
        agentVersion = md.versionStr(agent);
    } else {
        // if we are on desktop we don't get any user agent string back from
        // mobile detect. So we just try out the most common browsers.
        // This is just an adhoc heuristic and is not thoroughly tested against all
        // possible user agent strings. But desktop should be not that common anyhow for now.
        [agent, agentVersion] = md.versionStr('Firefox')
            ? ['Firefox', md.versionStr('Firefox')]
            : md.versionStr('Safari') && md.versionStr('Chrome')
              ? ['Chrome', md.versionStr('Chrome')]
              : ['Safari', md.versionStr('Safari')];
    }

    return {
        type,
        agent,
        width,
        height,
        orientation,
        osVersion,
        agentVersion,
        touch: !!md.mobile() || !!md.tablet() || !!md.phone(),
        device: md.mobile() || 'desktop',
        os: md.os() || 'desktop',
        grade: md.mobileGrade(),
        detOrientation: orientation === 'LANDSCAPE' ? 'LANDSCAPE-RIGHT' : 'PORTRAIT',
        buildVersion: md.versionStr('Build'),
        mobileVersion: md.versionStr('Mobile'),
        webkitVersion: md.versionStr('Webkit'),
        geckoVersion: md.versionStr('Gecko'),
        keyboard: false,
        keybHeight: -1,
        isEmulated: false,
        devBrand: null,
        devSystem: null,
        devModel: null,
        devManufacturer: null,
    } as any;
}

export const deviceReducerInitialState: any = Immutable.fromJS(collectDeviceInfos(window));

/**
 * Reduces an action containing screen size and
 * determines type and orientation of the device.
 * Currently based on css width/height, which is
 * resolution-independent due to the viewport definition in index.html.
 *
 * @param state
 * @param action
 * @returns {any}
 */
export default function deviceData(state = deviceReducerInitialState, action: any = {}) {
    switch (action.type) {
        case DEVICESCREEN_CHANGED:
            return state.merge(action.payload);
        case NATIVEPORT_REL_DEVICEINFO:
            return state.merge(reliableDeviceInfoReceived(action.payload));
        default:
            return state;
    }
}

function reliableDeviceInfoReceived(payload: any) {
    const info: IReliableDeviceInfo = payload.info as IReliableDeviceInfo;
    const stateBlk: any = {
        width: info.width,
        height: info.height,
        orientation: info.orientation,
        detOrientation: info.detailedOrientation,
        keyboard: info.keyboard,
        keybHeight: info.keybHeight,
        type: info.isTablet ? DeviceType.TABLET : DeviceType.PHONE,
        touch: true,
        isEmulated: info.isEmulated,
        osVersion: info.osVersion,
        agent: 'reactnative',
        devBrand: info.brand,
        devManufacturer: info.manufacturer,
        devModel: info.model,
        devSystem: info.system,
        biometryType: info.biometryType,
        buildNumber: info.buildNumber,
    };
    if (info.system) {
        stateBlk.os = info.system;
    }
    return stateBlk;
}
